import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, CheckSquare } from 'lucide-react';
import { Capture } from '../../../types/capture';
import { getCaptures } from '../../../services/api/captures';
import { getCurrentUserId } from '../../../services/auth';
import { lessonsService } from '../services/lessonsService';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useAnchorTooltip } from '../hooks/useAnchorTooltip';
import { LessonTooltip } from './LessonTooltip';
import { LessonGuideShell } from './LessonGuideShell';

type Step = 'demo' | 'try' | 'done';

interface Lesson1GuideProps {
  onClose: () => void;
  /** 通知父組件 Seed Capture ID，讓 CaptureTimelineView 知道哪張卡要高亮 */
  onSeedCaptureLoaded?: (captureId: string | null) => void;
}

const STEP_TITLES: Record<Step, string> = {
  'demo': 'See how Orlo clears your head',
  'try': 'Your turn — add your own capture',
  'done': 'Done! Your tasks are ready.',
};

/**
 * Lesson1Guide
 *
 * Onboarding Lesson 1 的引導卡片（右下角浮動，與 LessonsPanel 同位置）。
 *
 * Step 1 (demo)：引導用戶點 Seed Capture 卡片的「To Task」按鈕
 * Step 2 (try)：引導用戶自己新增一條 Capture，再點 To Task
 * Step done：完成，導回 Today 頁
 *
 * 透過 eventBus (EVENTS.LESSON.CAPTURE_TO_TASK_COMPLETED) 監聽用戶完成操作。
 */
export function Lesson1Guide({ onClose, onSeedCaptureLoaded }: Lesson1GuideProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('demo');
  const [seedCapture, setSeedCapture] = useState<Capture | null>(null);
  const [isLoadingSeed, setIsLoadingSeed] = useState(true);

  // 用戶互動後隱藏對應 tooltip（透過 enabled 控制）
  const [seedTooltipHidden, setSeedTooltipHidden] = useState(false);
  const [inputTooltipHidden, setInputTooltipHidden] = useState(false);

  // 查詢 Seed Capture（example-gift）
  useEffect(() => {
    const userId = getCurrentUserId();
    if (!userId) {
      setIsLoadingSeed(false);
      onSeedCaptureLoaded?.(null);
      return;
    }

    getCaptures({ userId, processed: false })
      .then((captures) => {
        const seed = captures.find(
          (c) =>
            c.source?.type === 'user-onboarding' &&
            c.source?.templateId === 'example-gift'
        );
        const found = seed ?? null;
        setSeedCapture(found);
        onSeedCaptureLoaded?.(found?.id ?? null);

        // 沒有 Seed Capture，直接跳到 Step 2
        if (!found) {
          setStep('try');
        }
      })
      .catch(() => {
        setSeedCapture(null);
        onSeedCaptureLoaded?.(null);
        setStep('try');
      })
      .finally(() => setIsLoadingSeed(false));
  }, [onSeedCaptureLoaded]);

  // 監聯 CaptureCard 完成 Capture → Task 事件，推進 step
  useEffect(() => {
    return eventBus.subscribe(
      EVENTS.LESSON.CAPTURE_TO_TASK_COMPLETED,
      () => {
        setStep((prev) => {
          if (prev === 'demo') return 'try';
          if (prev === 'try') {
            lessonsService.markCompleted('lesson-1');
            return 'done';
          }
          return prev;
        });
        // step 切換後重置 tooltip 隱藏狀態
        setSeedTooltipHidden(false);
        setInputTooltipHidden(false);
      }
    );
  }, []);

  // 用戶點擊 To Task 按鈕時隱藏 seed tooltip
  useEffect(() => {
    return eventBus.subscribe(
      EVENTS.LESSON.CAPTURE_TO_TASK_OPENED,
      () => setSeedTooltipHidden(true)
    );
  }, []);

  // 用戶開始輸入時隱藏 input tooltip
  useEffect(() => {
    return eventBus.subscribe(
      EVENTS.LESSON.CAPTURE_INPUT_INTERACTED,
      () => setInputTooltipHidden(true)
    );
  }, []);

  // 行動版（< 640px）不顯示 seed Tooltip，與 Guide 卡片距離太近會遮擋內容
  const isDesktop = !useMediaQuery('(max-width: 639px)');

  const calcLeftBelow = useCallback(
    (rect: DOMRect): React.CSSProperties => ({
      position: 'fixed',
      top: rect.bottom + 12,
      left: rect.left,
    }),
    [],
  );

  const seedTooltipStyle = useAnchorTooltip({
    anchor: 'seed-to-task',
    enabled: isDesktop && step === 'demo' && !seedTooltipHidden,
    calcStyle: calcLeftBelow,
    scrollIntoView: true,
    initialDelay: 600,
  });

  const inputTooltipStyle = useAnchorTooltip({
    anchor: 'capture-input',
    enabled: step === 'try' && !inputTooltipHidden,
    calcStyle: calcLeftBelow,
    initialDelay: 600,
  });

  // 完成：回到 Today
  const handleFinish = useCallback(() => {
    navigate('/today');
  }, [navigate]);

  const stepLabel =
    step === 'demo' ? 'Step 1 / 2' : step === 'try' ? 'Step 2 / 2' : 'Done';

  return (
    <>
      <LessonGuideShell
        lessonId="lesson-1"
        ariaLabel="Lesson 1: Clear your head"
        subtitle={`Clear your head · ${stepLabel}`}
        title={STEP_TITLES[step]}
        onClose={onClose}
      >
        {step === 'demo' && (
          <>
            {isLoadingSeed ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 size={13} className="animate-spin" />
                Loading example...
              </div>
            ) : seedCapture ? (
              <p className="text-sm text-gray-500 leading-relaxed">
                There's a capture waiting. Click its{' '}
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-sky-600 text-white text-xs rounded font-medium">
                  <CheckSquare size={10} />
                  To Task
                </span>{' '}
                button to see Orlo in action.
              </p>
            ) : null}
          </>
        )}

        {step === 'try' && (
          <p className="text-sm text-gray-500 leading-relaxed">
            Now type something you've been putting off — any thought works. Then click{' '}
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-sky-600 text-white text-xs rounded font-medium">
              <CheckSquare size={10} />
              To Task
            </span>{' '}
            on it.
          </p>
        )}

        {step === 'done' && (
          <>
            <p className="text-sm text-gray-500 leading-relaxed">
              Your tasks are ready. Tomorrow morning, Orlo will help you decide which ones to tackle first.
            </p>
            <button
              onClick={handleFinish}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-sky-600 text-white text-sm font-medium py-2 hover:bg-sky-700 transition-colors"
            >
              Back to today
              <ArrowRight size={14} />
            </button>
          </>
        )}
      </LessonGuideShell>

      {seedTooltipStyle && <LessonTooltip style={seedTooltipStyle} arrowAlign="left">Click here to see Orlo in action</LessonTooltip>}
      {inputTooltipStyle && <LessonTooltip style={inputTooltipStyle} arrowAlign="left">Type anything, then click Capture</LessonTooltip>}
    </>
  );
}
